/**
 * DebugLogger — 结构化环形缓冲日志器（客户端 + 服务端通用）。
 *
 * 规格对齐 docs/volumes/13-devops/04-observability.md §2：
 *  - 级别数字：trace 10 · debug 20 · info 30 · warn 40 · error 50 · fatal 60
 *  - 结构化字段：level/time/service/version/instance/msg + event/roomId/matchId/playerId/...
 *  - 事件名沿用文档约定：room.created / match.started / move.applied / move.invalid.mustmove / match.ended
 *
 * 设计要点：
 *  - 纯逻辑、零 DOM / 零 Three 依赖 → 可被 client、server、AI、单测共用。
 *  - 环形缓冲（容量固定）→ 长跑不泄漏；`entries()` 返回快照供调试面板渲染。
 *  - `channel(name)` 派生子记录器，为**网络模式信道**（'net'）预留同一套采集管线：
 *    联机落地后只需把 WS 收发事件打到 `logger.channel('net')`，面板/导出零改动。
 *  - `subscribe()` 推送新条目 → 调试窗口可实时刷新。
 */
import type { Language } from '../config/theme.js';

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/** 级别 → pino 兼容数字（observability §2.1）。 */
export const LOG_LEVELS: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
};

export interface LogErr {
  type: string;
  message: string;
  stack?: string;
}

export interface LogFields {
  /** 事件名，如 `move.applied`。 */
  event?: string;
  roomId?: string;
  matchId?: string;
  playerId?: string;
  latencyMs?: number;
  err?: LogErr;
  [key: string]: unknown;
}

export interface LogEntry {
  /** 单调序号（自 1 起）。 */
  seq: number;
  time: number;
  level: LogLevel;
  levelNum: number;
  service: string;
  version: string;
  instance: string;
  /** 逻辑信道（'app' 默认，'net' 预留给网络模式）。 */
  channel: string;
  msg: string;
  fields: LogFields;
}

export interface LoggerOptions {
  service?: string;
  version?: string;
  instance?: string;
  level?: LogLevel;
  /** 环形缓冲容量，默认 500。 */
  capacity?: number;
  channel?: string;
  /** 是否同时镜像到 console（默认 true）。 */
  mirrorConsole?: boolean;
}

export interface Logger {
  readonly service: string;
  readonly version: string;
  readonly instance: string;
  readonly channel: string;

  trace(msg: string, fields?: LogFields): void;
  debug(msg: string, fields?: LogFields): void;
  info(msg: string, fields?: LogFields): void;
  warn(msg: string, fields?: LogFields): void;
  error(msg: string, fields?: LogFields): void;
  fatal(msg: string, fields?: LogFields): void;
  /** 以任意级别记录。 */
  log(level: LogLevel, msg: string, fields?: LogFields): void;

  /** 派生一个带信道标签的子记录器（共享同一环形缓冲）。 */
  channelOf(name: string): Logger;
  setLevel(level: LogLevel): void;
  getLevel(): LogLevel;
  /** 环形缓冲全部条目的快照（按时序）。 */
  entries(): readonly LogEntry[];
  /** 导出为可读文本（复制/下载用）。 */
  snapshot(): string;
  clear(): void;
  /** 订阅新条目；返回退订函数。 */
  subscribe(fn: (entry: LogEntry) => void): () => void;
}

const REDACT_KEY = /token|password|secret|authorization|cookie/i;

/** 递归脱敏：命中敏感键名的字段替换为 [REDACTED]（observability §2.3）。 */
function scrub(value: unknown, depth = 0): unknown {
  if (depth > 4 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = REDACT_KEY.test(k) ? '[REDACTED]' : scrub(v, depth + 1);
  }
  return out;
}

class RingLogger implements Logger {
  readonly service: string;
  readonly version: string;
  readonly instance: string;
  readonly channel: string;

  private level: LogLevel;
  private readonly capacity: number;
  private readonly mirror: boolean;
  private readonly buf: LogEntry[] = [];
  private readonly subs = new Set<(e: LogEntry) => void>();
  private seq = 0;

  constructor(
    opts: LoggerOptions,
    private readonly root: RingLogger | null = null
  ) {
    const base = root ?? this;
    this.service = opts.service ?? base?.service ?? 'marble-client';
    this.version = opts.version ?? base?.version ?? 'dev';
    this.instance = opts.instance ?? base?.instance ?? 'local';
    this.level = opts.level ?? base?.level ?? 'debug';
    this.capacity = opts.capacity ?? base?.capacity ?? 500;
    this.mirror = opts.mirrorConsole ?? base?.mirror ?? true;
    this.channel = opts.channel ?? 'app';
  }

  /** 根记录器（信道子记录器共享其缓冲 / 订阅 / 序号 / 级别）。 */
  private get store(): RingLogger {
    return this.root ?? this;
  }

  log(level: LogLevel, msg: string, fields: LogFields = {}): void {
    const s = this.store;
    if (LOG_LEVELS[level] < LOG_LEVELS[s.level]) return;
    const entry: LogEntry = {
      seq: ++s.seq,
      time: Date.now(),
      level,
      levelNum: LOG_LEVELS[level],
      service: this.service,
      version: this.version,
      instance: this.instance,
      channel: this.channel,
      msg,
      fields: scrub(fields) as LogFields,
    };
    s.buf.push(entry);
    if (s.buf.length > s.capacity) s.buf.splice(0, s.buf.length - s.capacity);
    if (s.mirror) {
      const fn =
        level === 'fatal' || level === 'error'
          ? console.error
          : level === 'warn'
            ? console.warn
            : console.info;
      fn.call(console, `[${level}] ${this.channel} ${msg}`, entry.fields);
    }
    for (const fn of s.subs) {
      try {
        fn(entry);
      } catch {
        /* 订阅者异常不得影响日志主链 */
      }
    }
  }

  trace(m: string, f?: LogFields): void {
    this.log('trace', m, f);
  }
  debug(m: string, f?: LogFields): void {
    this.log('debug', m, f);
  }
  info(m: string, f?: LogFields): void {
    this.log('info', m, f);
  }
  warn(m: string, f?: LogFields): void {
    this.log('warn', m, f);
  }
  error(m: string, f?: LogFields): void {
    this.log('error', m, f);
  }
  fatal(m: string, f?: LogFields): void {
    this.log('fatal', m, f);
  }

  channelOf(name: string): Logger {
    return new RingLogger({ channel: name }, this.root ?? this);
  }

  setLevel(l: LogLevel): void {
    this.store.level = l;
  }
  getLevel(): LogLevel {
    return this.store.level;
  }
  entries(): readonly LogEntry[] {
    return [...this.store.buf];
  }
  clear(): void {
    this.store.buf.length = 0;
  }

  snapshot(): string {
    return this.store.buf
      .map((e) => {
        const ts = new Date(e.time).toISOString().slice(11, 23);
        const extra = Object.keys(e.fields).length ? ` ${JSON.stringify(e.fields)}` : '';
        return `${String(e.seq).padStart(5, '0')} ${ts} ${e.level.toUpperCase().padEnd(5)} [${e.channel}] ${e.msg}${extra}`;
      })
      .join('\n');
  }

  subscribe(fn: (e: LogEntry) => void): () => void {
    const s = this.store;
    s.subs.add(fn);
    return () => s.subs.delete(fn);
  }
}

/** 创建一个结构化环形缓冲日志器。 */
export function createLogger(opts: LoggerOptions = {}): Logger {
  return new RingLogger(opts);
}

/** 由任意 Error 生成结构化 `err` 字段。 */
export function toLogErr(err: unknown): LogErr {
  if (err instanceof Error) {
    const out: LogErr = { type: err.name, message: err.message };
    if (err.stack) out.stack = err.stack;
    return out;
  }
  return { type: 'Unknown', message: String(err) };
}

/** 级别 → 简短本地化标签（调试面板用）。 */
export function levelLabel(level: LogLevel, lang: Language): string {
  const zh = lang === 'zh';
  switch (level) {
    case 'trace':
      return zh ? '追踪' : 'TRACE';
    case 'debug':
      return zh ? '调试' : 'DEBUG';
    case 'info':
      return zh ? '信息' : 'INFO';
    case 'warn':
      return zh ? '警告' : 'WARN';
    case 'error':
      return zh ? '错误' : 'ERROR';
    case 'fatal':
      return zh ? '致命' : 'FATAL';
  }
}
